import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { groupService } from "@/services";
import type { GroupStatus, KametiGroup } from "@/services/types";
import { GROUP_STATUSES, GROUP_STATUS_LABEL, GROUP_STATUS_BADGE } from "@/config/groups";
import { canDeleteGroup } from "@/config/roles";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { FilterBar } from "@/components/feature/FilterBar";
import { DataTable, type SortState, type DataTableColumn } from "@/components/feature/DataTable";
import { Button } from "@/components/base/Button";
import { Select, type SelectOption } from "@/components/base/Select";
import { Badge } from "@/components/base/Badge";
import { Dropdown } from "@/components/base/Dropdown";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { Modal } from "@/components/base/Modal";
import { Textarea } from "@/components/base/Textarea";
import { completedCycles, nextAuctionDate } from "@/utils/group";
import { formatDate } from "@/utils/format";

interface ViewState {
  search: string;
  status: string;
  sort: SortState | null;
  page: number;
  pageSize: number;
}

const VIEW_KEY = "kameti.groups.view";

function loadView(): ViewState {
  try {
    const raw = sessionStorage.getItem(VIEW_KEY);
    if (raw) return { search: "", status: "", sort: null, page: 1, pageSize: 10, ...JSON.parse(raw) };
  } catch {
    // ignore
  }
  return { search: "", status: "", sort: null, page: 1, pageSize: 10 };
}

function ProgressCell({ group }: { group: KametiGroup }) {
  const done = completedCycles(group);
  const total = group.memberCount;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  if (group.status === "draft") {
    return <span className="text-xs text-foreground-400">Not started</span>;
  }
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-background-200">
        <div className="h-full rounded-full bg-accent-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="tnum whitespace-nowrap text-xs text-foreground-600">
        {done}/{total}
      </span>
    </div>
  );
}

export default function GroupsPage() {
  const navigate = useNavigate();
  const { isViewer, role } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const canDelete = canDeleteGroup(role);

  const [initialView] = useState<ViewState>(loadView);
  const [search, setSearch] = useState(initialView.search);
  const [status, setStatus] = useState(initialView.status);
  const [tableState, setTableState] = useState<{ sort: SortState | null; page: number; pageSize: number }>({
    sort: initialView.sort,
    page: initialView.page,
    pageSize: initialView.pageSize,
  });

  const [groups, setGroups] = useState<KametiGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<KametiGroup | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setGroups(await groupService.listGroups());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load groups.");
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    try {
      sessionStorage.setItem(VIEW_KEY, JSON.stringify({ search, status, ...tableState }));
    } catch {
      // ignore
    }
  }, [search, status, tableState]);

  const activeFilterCount = [search, status].filter(Boolean).length;

  const filtered = useMemo(() => {
    if (!groups) return null;
    let out = groups;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((g) => g.name.toLowerCase().includes(q) || g.groupCode.toLowerCase().includes(q));
    }
    if (status) out = out.filter((g) => g.status === status);
    return out;
  }, [groups, search, status]);

  const handleClear = () => {
    setSearch("");
    setStatus("");
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await groupService.cancelGroup(cancelTarget.id, cancelReason.trim() || "No reason provided.", "Staff User");
      toast.success("Group cancelled", `${cancelTarget.name} is now cancelled.`);
      setCancelTarget(null);
      setCancelReason("");
      await load();
    } catch (err) {
      toast.error("Could not cancel group", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const handleDelete = async (g: KametiGroup) => {
    const ok = await confirm({
      title: "Delete Group",
      message: `Are you sure you want to permanently delete group "${g.name}" (${g.groupCode})? This action cannot be undone.`,
      confirmLabel: "Delete Group",
      destructive: true,
      dangerText: "All cycles, member enrollments, and transactions for this group will be permanently removed.",
    });
    if (!ok) return;

    try {
      await groupService.deleteGroup(g.id);
      toast.success("Group deleted", `${g.name} was permanently removed.`);
      await load();
    } catch (err) {
      toast.error("Could not delete group", err instanceof Error ? err.message : "Please try again.");
    }
  };

  const statusOptions: SelectOption[] = [
    { value: "", label: "All statuses" },
    ...GROUP_STATUSES.map((s) => ({ value: s.value, label: s.label })),
  ];

  const columns: DataTableColumn<KametiGroup>[] = [
    {
      id: "groupCode",
      header: "Group Code",
      accessor: (g) => <span className="tnum text-xs font-semibold text-primary-700">{g.groupCode}</span>,
      sortValue: (g) => g.groupCode,
      sortable: true,
    },
    {
      id: "name",
      header: "Group Name",
      accessor: (g) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground-900">{g.name}</span>
          {g.status === "draft" ? <Badge variant="pending">Draft</Badge> : null}
        </div>
      ),
      sortValue: (g) => g.name,
      sortable: true,
    },
    {
      id: "installment",
      header: "Monthly Installment",
      align: "right",
      accessor: (g) => <CurrencyDisplay amount={g.monthlyInstallment} hideSymbol />,
      sortValue: (g) => g.monthlyInstallment,
      sortable: true,
    },
    {
      id: "members",
      header: "Total Members",
      align: "right",
      accessor: (g) => <span className="tnum">{g.memberCount}</span>,
      sortValue: (g) => g.memberCount,
      sortable: true,
    },
    {
      id: "chitValue",
      header: "Chit Value",
      align: "right",
      accessor: (g) => <CurrencyDisplay amount={g.chitValue} hideSymbol />,
      sortValue: (g) => g.chitValue,
      sortable: true,
      footerValue: (rows) => <CurrencyDisplay amount={rows.reduce((s, g) => s + g.chitValue, 0)} hideSymbol />,
    },
    {
      id: "startDate",
      header: "Start Date",
      accessor: (g) => <span className="tnum">{formatDate(g.startDate)}</span>,
      sortValue: (g) => g.startDate,
      sortable: true,
    },
    {
      id: "progress",
      header: "Progress",
      accessor: (g) => <ProgressCell group={g} />,
      sortValue: (g) => completedCycles(g),
      sortable: true,
    },
    {
      id: "nextAuction",
      header: "Next Auction",
      accessor: (g) => {
        const next = nextAuctionDate(g);
        return next ? <span className="tnum">{formatDate(next)}</span> : <span className="text-foreground-400">—</span>;
      },
      sortValue: (g) => nextAuctionDate(g) ?? "",
      sortable: true,
    },
    {
      id: "status",
      header: "Status",
      accessor: (g) => <Badge variant={GROUP_STATUS_BADGE[g.status]} dot>{GROUP_STATUS_LABEL[g.status]}</Badge>,
      sortValue: (g) => g.status,
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      align: "right",
      width: "56px",
      hideable: false,
      accessor: (g) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            align="right"
            width="w-56"
            trigger={() => (
              <button type="button" aria-label="Row actions" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800">
                <i className="ri-more-2-line text-lg" />
              </button>
            )}
            items={[
              { key: "view", label: "View group", icon: "ri-eye-line", onClick: () => navigate(`/groups/${g.id}`) },
              {
                key: "edit",
                label: "Edit",
                icon: "ri-pencil-line",
                disabled: isViewer || g.status !== "draft",
                onClick: () => navigate(`/groups/${g.id}/edit`),
              },
              {
                key: "enroll",
                label: "Manage enrolment",
                icon: "ri-user-add-line",
                disabled: isViewer || (g.status !== "draft" && g.status !== "active"),
                onClick: () => navigate(`/groups/${g.id}/enrollment`),
              },
              {
                key: "divider",
                label: "",
                divider: true,
              },
              {
                key: "cancel",
                label: "Cancel group",
                icon: "ri-close-circle-line",
                danger: true,
                disabled: isViewer || g.status === "completed" || g.status === "cancelled",
                onClick: () => {
                  setCancelReason("");
                  setCancelTarget(g);
                },
              },
              ...(canDelete ? [
                {
                  key: "divider-delete",
                  label: "",
                  divider: true,
                },
                {
                  key: "delete",
                  label: "Delete group",
                  icon: "ri-delete-bin-line",
                  danger: true,
                  onClick: () => handleDelete(g),
                },
              ] : []),
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Groups" isLast />
          </>
        }
        title="Groups"
        subtitle={groups ? `${groups.length} chit groups` : "Loading groups…"}
        actions={
          <Button icon={<i className="ri-add-line" />} disabled={isViewer} onClick={() => navigate("/groups/new")}>
            Create Group
          </Button>
        }
      />

      <div className="mb-3">
        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: "Search group name or code…", loading: groups === null }}
          filters={[{ id: "status", control: <Select value={status} onChange={setStatus} options={statusOptions} /> }]}
          activeCount={activeFilterCount}
          onClear={handleClear}
        />
      </div>

      <DataTable<KametiGroup>
        columns={columns}
        rows={filtered}
        rowKey={(g) => g.id}
        loading={groups === null}
        error={error}
        onRetry={load}
        emptyMessage="No groups found"
        emptyDescription="Try adjusting your filters, or create a new group."
        onRowClick={(g) => navigate(`/groups/${g.id}`)}
        showTotals
        totalLabel="Totals"
        exportable
        exportFilename="groups"
        initialSort={tableState.sort ?? { id: "groupCode", dir: "asc" }}
        initialPage={tableState.page}
        initialPageSize={tableState.pageSize}
        onViewStateChange={(s) => setTableState(s)}
      />

      <Modal
        open={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        title="Cancel this group?"
        description={cancelTarget ? `${cancelTarget.name} (${cancelTarget.groupCode}) will be cancelled and removed from active management.` : ""}
        footer={
          <>
            <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={cancelling}>
              Keep group
            </Button>
            <Button variant="danger" icon={<i className="ri-close-circle-line" />} loading={cancelling} onClick={handleCancel}>
              Cancel group
            </Button>
          </>
        }
      >
        <Textarea
          label="Reason for cancellation"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Explain why this group is being cancelled…"
          rows={4}
          maxLength={500}
        />
      </Modal>
    </div>
  );
}